
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, useColor, useBrand } from '../context/ColorContext';
import { DUFA_DATA } from '../data/constants'; // DUFA_DATA as fallback
import { ChevronLeft, ShoppingBag, Shield, Zap, Droplets, Info, Box, Clock, TrendingUp, X, CheckCircle, Sparkles } from 'lucide-react';
import FanDeck from '../components/home/FanDeck/FanDeck';
import HomeVisualizer from '../components/home/HomeVisualizer';
import Chatbot from '../components/product/Chatbot';

const UrunDetail = ({ onOpenVisualizer }) => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { activeColor, isFanDeckOpen, setIsFanDeckOpen, brandData, isDbLoading } = useColor();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';
    const [startY, setStartY] = useState(0);
    const [isFanDeckMinimized, setIsFanDeckMinimized] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isTdsModalOpen, setIsTdsModalOpen] = useState(false);
    const [isTdsSubmitted, setIsTdsSubmitted] = useState(false);

    const handleTouchStart = (e) => {
        if (isChatOpen) return;
        setStartY(e.touches[0].clientY);
    };

    const handleTouchEnd = (e) => {
        if (isChatOpen) return;
        const endY = e.changedTouches[0].clientY;
        const diff = startY - endY;

        if (diff > 50) {
            setIsFanDeckOpen(true);
        } else if (diff < -50) {
            setIsFanDeckOpen(false);
        }
    };

    // Auto-close FanDeck when chat opens, and restore when it closes
    useEffect(() => {
        if (isChatOpen) {
            setIsFanDeckOpen(false);
            setIsFanDeckMinimized(true);
        } else {
            // Restore FanDeck state when chat is closed
            setIsFanDeckMinimized(false);
        }
    }, [isChatOpen, setIsFanDeckOpen]);

    // Product detail pages should start with kartela hidden.
    useEffect(() => {
        setIsFanDeckOpen(false);
    }, [slug, setIsFanDeckOpen]);

    // UseMemo for stability and performance
    const product = React.useMemo(() => {
        return brandData.PRODUCTS.find(p => p.slug === slug || String(p.id) === String(slug));
    }, [brandData.PRODUCTS, slug]);

    // Tech Specs Memo
    const techSpecs = React.useMemo(() => [
        { icon: <Shield size={20} />, title: "Dayanıklılık", value: product?.techDurability || "Üstün Koruma" },
        { icon: <Droplets size={20} />, title: "Yapı", value: product?.techStructure || "Profesyonel" },
        { icon: <Zap size={20} />, title: "Görünüm", value: product?.techAppearance || "Dekoratif" },
        { icon: <Info size={20} />, title: "Özellik", value: product?.techFeatures || "Yeni Nesil" }
    ], [product]);

    // Helper to get RGB for glow
    const primaryRgb = React.useMemo(() => {
        const hex = activeBrand.primaryColor;
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '90, 166, 104';
    }, [activeBrand.primaryColor]);

    // ... (useEffect for SEO omitted for brevity in replacement chunk but kept in file)

    // Loading State: Prevent demo flash
    if (isDbLoading && brandData.PRODUCTS.length === 0) {
        return (
            <div className="fixed inset-0 bg-white dark:bg-slate-950 flex flex-col items-center justify-center z-50">
                <div className="relative">
                    <div className="w-20 h-20 border-4 border-slate-100 dark:border-white/5 rounded-full animate-spin border-t-primary-500"
                        style={{ borderTopColor: activeBrand.primaryColor }} />
                    <Box className="absolute inset-0 m-auto text-slate-200 dark:text-white/10 animate-pulse" size={32} />
                </div>
                <p className="mt-6 text-sm font-bold tracking-widest text-slate-400 dark:text-white/20 uppercase animate-pulse">
                    Ürün Bilgileri Yükleniyor...
                </p>
            </div>
        );
    }

    if (!product) {
        return (
            <div className={`min-h-screen pt-32 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <h2 className="text-2xl font-bold mb-4">Ürün bulunamadı.</h2>
                <button
                    onClick={() => navigate('/urunler')}
                    className="px-6 py-2 text-white rounded-full uppercase tracking-widest font-bold"
                    style={{ backgroundColor: activeBrand.primaryColor }}
                >
                    Ürünlere Dön
                </button>
            </div>
        );
    }

    return (
        <div className={`relative min-h-screen pt-32 pb-48 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {/* MOBILE BLUR OVERLAY */}
            <div
                className={`fixed inset-0 z-40 lg:hidden transition-all duration-700 pointer-events-none
                    ${(isFanDeckOpen || isChatOpen) ? 'opacity-100 backdrop-blur-xl bg-black/35' : 'opacity-0 backdrop-blur-none bg-transparent'}
                `}
            />

            <div className="container mx-auto px-4 md:px-0 lg:pl-32 relative pointer-events-none">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className={`flex items-center gap-2 mb-12 opacity-60 hover:opacity-100 transition-opacity uppercase text-xs font-bold tracking-[0.2em] pointer-events-auto`}
                >
                    <ChevronLeft size={20} /> Geri Dön
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start pointer-events-none relative">
                    {/* Left Column: Fixed space, sticky content */}
                    <div className="lg:col-span-5 lg:sticky lg:top-32 self-start pointer-events-none z-10">
                        <div className={`relative aspect-square rounded-[3.5rem] flex items-center justify-center p-12 overflow-hidden border backdrop-blur-2xl pointer-events-auto transform-gpu ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-black/5'}`}>
                            <div className={`absolute inset-0 opacity-20`} style={{ background: `linear-gradient(to right bottom, ${activeBrand.primaryColor}, transparent)` }} />
                            <img
                                src={product.image}
                                alt={product.name}
                                loading="eager"
                                className={`relative z-10 w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.3)] transform-gpu hover:scale-105 transition-transform duration-700 backface-hidden`}
                            />
                        </div>

                        {/* Desktop button removed to move into FanDeck block */}

                        {/* Chatbot Integration */}
                        <div className="pointer-events-auto relative z-[55]">
                            <Chatbot product={product} isOpen={isChatOpen} onToggle={setIsChatOpen} />
                        </div>
                    </div>

                    {/* Right Column: Detailed Info */}
                    <div className="lg:col-span-7 space-y-12 relative pointer-events-none z-50">
                        {/* Title & Description */}
                        {(product.longDesc || product.desc) && (
                            <div className="space-y-6 pointer-events-auto">
                                <div className="flex flex-wrap gap-2">
                                    {product.categoryNames?.map((catName, idx) => (
                                        <span key={idx} className={`inline-block px-4 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${isDark ? 'border-white/20 text-white/60' : 'border-black/10 text-slate-500'}`}>
                                            {catName}
                                        </span>
                                    ))}
                                </div>
                                <h1 className="text-4xl md:text-7xl font-black uppercase tracking-tighter leading-none">
                                    {product.name?.toUpperCase()}
                                </h1>
                                {product.productCode && (
                                    <p className="text-xl md:text-2xl font-bold opacity-60 tracking-wider">
                                        Ürün Kodu: {product.productCode}
                                    </p>
                                )}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold uppercase tracking-widest" style={{ color: activeBrand.primaryColor }}>Ürün Tanımı</h3>
                                    <p className="text-xl font-light opacity-90 leading-relaxed whitespace-pre-wrap">
                                        {product.longDesc || product.desc}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Quick Specs Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pointer-events-auto">
                            {techSpecs.map((spec, idx) => (
                                <div key={idx} className={`p-6 rounded-3xl border backdrop-blur-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-black/5'}`}>
                                    <div className="mb-3" style={{ color: activeBrand.primaryColor }}>{spec.icon}</div>
                                    <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">{spec.title}</h4>
                                    <p className="font-bold">{spec.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Sections from Demo Content */}
                        {(product.features || product.application) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pointer-events-none">
                                {product.features && (
                                    <section className={`p-8 rounded-[2.5rem] border backdrop-blur-2xl space-y-4 pointer-events-auto ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-black/5'}`}>
                                        <h3 className="text-lg font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: activeBrand.primaryColor }}>
                                            <Shield size={18} /> ÖZELLİKLERİ
                                        </h3>
                                        <p className="text-sm font-light opacity-70 leading-relaxed text-justify whitespace-pre-wrap">
                                            {product.features}
                                        </p>
                                    </section>
                                )}

                                {product.application && (
                                    <section className={`p-8 rounded-[2.5rem] border backdrop-blur-2xl space-y-4 pointer-events-auto ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-black/5'}`}>
                                        <h3 className="text-lg font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: activeBrand.primaryColor }}>
                                            <Droplets size={18} /> UYGULAMA
                                        </h3>
                                        <p className="text-sm font-light opacity-70 leading-relaxed text-justify whitespace-pre-wrap">
                                            {product.application?.length > 300 ? product.application.substring(0, 300) + '...' : product.application}
                                        </p>
                                    </section>
                                )}
                            </div>
                        )}

                        {/* Grid for small info blocks */}
                        {(product.dryingTime || product.consumption || product.storage) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pointer-events-auto">
                                {product.dryingTime && (
                                    <div className={`p-8 rounded-[2rem] border backdrop-blur-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'}`}>
                                        <h3 className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-4 flex items-center gap-2">
                                            <Clock size={14} /> KURUMA SÜRESİ
                                        </h3>
                                        <p className="text-sm font-bold leading-relaxed">{product.dryingTime}</p>
                                    </div>
                                )}
                                {product.consumption && (
                                    <div className={`p-8 rounded-[2rem] border backdrop-blur-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'}`}>
                                        <h3 className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-4 flex items-center gap-2">
                                            <TrendingUp size={14} /> SARFİYAT
                                        </h3>
                                        <p className="text-sm font-bold leading-relaxed">{product.consumption}</p>
                                    </div>
                                )}
                                {product.storage && (
                                    <div className={`p-8 rounded-[2rem] border backdrop-blur-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'}`}>
                                        <h3 className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-4 flex items-center gap-2">
                                            <Box size={14} /> DEPOLAMA
                                        </h3>
                                        <p className="text-sm font-bold leading-relaxed">{product.storage}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="pt-10 flex flex-col sm:flex-row gap-6 pointer-events-auto">
                            <button
                                className="flex-1 py-6 text-white rounded-[2rem] font-black uppercase tracking-widest transition-all hover:scale-105 shadow-2xl flex items-center justify-center gap-3"
                                style={{ backgroundColor: activeBrand.primaryColor, boxShadow: `0 20px 25px -5px ${activeBrand.primaryColor}40` }}
                            >
                                <ShoppingBag size={24} /> Satın Al
                            </button>
                            <button
                                onClick={() => setIsTdsModalOpen(true)}
                                className={`flex-1 py-6 rounded-[2rem] font-black uppercase tracking-widest border transition-all ${isDark ? 'border-white/20 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'}`}>
                                TDS Talep Et
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Bottom: FanDeck Mobile Drawer Logic - Smooth CSS Transition */}
            {(product.kartelas === undefined || product.kartelas.length > 0) && (
                <div
                    className={`fixed left-0 right-0 h-[360px] sm:h-[440px] transition-all duration-700 ease-in-out flex flex-col pointer-events-none z-50 lg:z-30 bottom-0
                        ${isFanDeckOpen && !isChatOpen ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none translate-y-full'}
                    `}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="container mx-auto px-4 md:px-0 lg:pl-32 h-full relative">
                        <div className="grid grid-cols-1 lg:grid-cols-12 h-full items-end pb-8">
                            <div className="lg:col-span-5 pointer-events-auto h-full w-full relative">
                                <FanDeck
                                    isMobile={window.innerWidth < 1024}
                                    enableWheel={true}
                                    activeCardExtra={
                                        product.visualizerWebhookUrl ? (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); window.open(product.visualizerWebhookUrl, '_blank'); }}
                                                className="px-4 py-3 rounded-xl font-black uppercase tracking-widest text-[9px] bg-indigo-600 text-white shadow-2xl transition-all hover:scale-110 active:scale-95 flex items-center justify-center gap-2 group whitespace-nowrap"
                                            >
                                                <Sparkles size={12} className="group-hover:animate-pulse" />
                                                <span>AI GÖRSEL</span>
                                            </button>
                                        ) : null
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Kartela Icon (replaces bottom drawer trigger) */}
            {(product.kartelas === undefined || product.kartelas.length > 0) && !isFanDeckOpen && !isChatOpen && (() => {
                // Pick a small palette around the active color for the mini fan
                const palette = ((product.kartelas?.[0]?.colors) || brandData.COLORS || [])
                    .map(c => c.hex || c.code)
                    .filter(Boolean);
                const fallback = [activeBrand.primaryColor, '#E5DDD0', activeColor.code, '#1F2937'];
                let chips;
                if (palette.length >= 3) {
                    const idx = Math.max(0, palette.findIndex(c =>
                        (c || '').toLowerCase() === (activeColor.code || '').toLowerCase()
                    ));
                    const pick = (off) => palette[((idx + off) % palette.length + palette.length) % palette.length];
                    chips = [pick(-2), pick(-1), activeColor.code, pick(1)];
                } else {
                    chips = fallback;
                }
                const fan = [
                    { rotate: -22, x: -9, z: 1 },
                    { rotate: -8,  x: -3, z: 2 },
                    { rotate: 8,   x: 3,  z: 4 }, // active sits on top
                    { rotate: 22,  x: 9,  z: 3 },
                ];

                return (
                    <button
                        onClick={() => {
                            setIsFanDeckOpen(true);
                            setIsFanDeckMinimized(false);
                        }}
                        className={`fixed left-4 bottom-14 z-[55] flex flex-col items-center gap-1.5 px-3 pt-2 pb-2.5 rounded-3xl backdrop-blur-2xl border shadow-2xl shadow-black/20 hover:scale-105 active:scale-95 transition-all pointer-events-auto animate-fadeIn ${isDark ? 'bg-slate-800/85 border-white/10' : 'bg-white/90 border-black/5'}`}
                        aria-label="Kartelayı Aç"
                    >
                        {/* Mini fanned kartela */}
                        <div className="relative w-14 h-14 flex items-center justify-center">
                            <div
                                className="absolute inset-0 blur-xl opacity-25 rounded-full animate-pulse"
                                style={{ backgroundColor: activeBrand.primaryColor }}
                            />
                            {fan.map((p, i) => (
                                <div
                                    key={i}
                                    className="absolute top-1/2 left-1/2 rounded-[6px] ring-1 ring-black/10 dark:ring-white/15"
                                    style={{
                                        width: '13px',
                                        height: '40px',
                                        backgroundColor: chips[i] || '#ccc',
                                        transform: `translate(-50%, -50%) translateX(${p.x}px) rotate(${p.rotate}deg)`,
                                        zIndex: p.z,
                                        boxShadow: i === 2 ? '0 5px 12px rgba(0,0,0,0.3)' : '0 2px 5px rgba(0,0,0,0.18)',
                                    }}
                                />
                            ))}
                        </div>
                        <div className="flex flex-col items-center leading-tight">
                            <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Kartela
                            </span>
                            <span className={`text-[8px] font-bold uppercase tracking-tight max-w-[80px] truncate ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                                {activeColor.name}
                            </span>
                        </div>
                    </button>
                );
            })()}

            {/* Close Kartela Button - only when kartela is open */}
            {(product.kartelas === undefined || product.kartelas.length > 0) && isFanDeckOpen && !isChatOpen && (
                <button
                    onClick={() => setIsFanDeckOpen(false)}
                    className={`fixed left-4 bottom-14 z-[120] flex items-center gap-2 px-4 py-3 rounded-full backdrop-blur-2xl border shadow-2xl shadow-black/30 active:scale-95 transition-all pointer-events-auto animate-fadeIn ${isDark ? 'bg-slate-800/90 border-white/10 text-white' : 'bg-white/95 border-black/5 text-slate-900'}`}
                    aria-label="Kartelayı Kapat"
                >
                    <X size={18} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Kapat</span>
                </button>
            )}

            {/* Inline Visualizer Area - shown above the kartela when it is open */}
            {(product.kartelas === undefined || product.kartelas.length > 0) && isFanDeckOpen && !isChatOpen && (
                <div className="fixed inset-x-0 top-24 lg:top-28 bottom-[340px] sm:bottom-[420px] z-[40] flex items-center justify-center px-3 pointer-events-none animate-fadeIn">
                    <div className="relative w-full h-full max-w-[min(95vw,900px)] pointer-events-auto rounded-[2rem] overflow-hidden shadow-2xl">
                        <HomeVisualizer
                            initialIndex={0}
                            aiPrompt={product.kartelas?.[0]?.aiPrompt || product.aiPrompt || null}
                            onOpenVisualizer={() => { /* already inline, no-op */ }}
                        />
                    </div>
                </div>
            )}

            {/* TDS Modal Form */}
            {isTdsModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
                    <div className={`relative w-full max-w-md p-8 rounded-[2rem] shadow-2xl overflow-hidden ${isDark ? 'bg-slate-900 border border-white/10 text-white' : 'bg-white border border-black/10 text-slate-900'}`}>
                        <button
                            onClick={() => setIsTdsModalOpen(false)}
                            className={`absolute top-6 right-6 p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'} z-10`}
                        >
                            <X size={20} />
                        </button>

                        {isTdsSubmitted ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center mx-auto mb-6">
                                    <CheckCircle size={32} />
                                </div>
                                <h4 className="text-xl font-black uppercase tracking-tighter mb-2" style={{ color: activeBrand.primaryColor }}>Talebiniz Alındı</h4>
                                <p className={`text-sm font-light mb-8 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>TDS (Teknik Veri Sayfası) dökümanı en kısa sürede e-posta adresinize gönderilecektir.</p>
                                <button
                                    onClick={() => {
                                        setIsTdsModalOpen(false);
                                        setTimeout(() => setIsTdsSubmitted(false), 300);
                                    }}
                                    className="px-8 py-3 rounded-xl font-black uppercase tracking-widest text-white transition-transform hover:scale-105"
                                    style={{ backgroundColor: activeBrand.primaryColor }}
                                >
                                    KAPAT
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-2xl font-black uppercase tracking-tighter mb-2" style={{ color: activeBrand.primaryColor }}>TDS Talep Formu</h3>
                                <p className={`text-xs font-light mb-6 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Lütfen ürünün teknik dökümanını (TDS) talep etmek için aşağıdaki formu eksiksiz doldurun.</p>

                                <form className="space-y-4" onSubmit={(e) => {
                                    e.preventDefault();
                                    setIsTdsSubmitted(true);
                                }}>
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Adı Soyadı</label>
                                        <input required type="text" className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-black/20 border-white/10 focus:border-white/30 text-white' : 'bg-slate-50 border-black/5 focus:border-black/20 text-slate-900'} outline-none transition-colors text-sm font-medium`} placeholder="Adınız Soyadınız" />
                                    </div>
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>E-Posta Adresi</label>
                                        <input required type="email" className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-black/20 border-white/10 focus:border-white/30 text-white' : 'bg-slate-50 border-black/5 focus:border-black/20 text-slate-900'} outline-none transition-colors text-sm font-medium`} placeholder="ornek@sirket.com" />
                                    </div>
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Şirket Bilgisi</label>
                                        <input required type="text" className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'bg-black/20 border-white/10 focus:border-white/30 text-white' : 'bg-slate-50 border-black/5 focus:border-black/20 text-slate-900'} outline-none transition-colors text-sm font-medium`} placeholder="Şirketinizin Adı" />
                                    </div>
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Mesajınız</label>
                                        <textarea required rows="3" className={`w-full px-4 py-3 rounded-xl border resize-none ${isDark ? 'bg-black/20 border-white/10 focus:border-white/30 text-white' : 'bg-slate-50 border-black/5 focus:border-black/20 text-slate-900'} outline-none transition-colors text-sm font-medium`} placeholder="Talebiniz ile ilgili detayları belirtebilirsiniz..." />
                                    </div>

                                    <button type="submit" className="w-full py-4 mt-2 rounded-xl font-black uppercase tracking-widest text-white transition-transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2" style={{ backgroundColor: activeBrand.primaryColor }}>
                                        <CheckCircle size={18} />
                                        Talep Et
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UrunDetail;
